import { CommonModule } from '@angular/common';
import { AfterViewInit, Component, Input, OnDestroy } from '@angular/core';
import { Subject, Subscription, switchMap, takeUntil, of } from 'rxjs';

import { CustomPayloadService } from '../shared/dashboard-payload-services/custom-payload.service';
import { ChartFilterHandlerService } from '@shared-lib/shared-services/chart-filter-handler.service';
import { GlobalFilterService } from '@shared-lib/shared-services/global-filter-service';
import { formatStat, snakeCaseToTitleCase } from '@shared-lib/utils/format.utils';

import { MatIconModule } from '@angular/material/icon';

@Component({
  selector: 'app-number-stats',
  standalone: true,
  imports: [CommonModule, MatIconModule],
  templateUrl: './number-stats.component.html',
  styleUrl: './number-stats.component.scss'
})
export class NumberStatsComponent implements AfterViewInit, OnDestroy {
  @Input() data!: any;
  stat!: string;
  private filterSub!: Subscription;
  private unSubscribe$: Subject<void> = new Subject();
  public hasError = false;
  public errorMessage = '';

  constructor(
    private configService: CustomPayloadService,
    private globalFilterService: GlobalFilterService,
    private chartFilterHandler: ChartFilterHandlerService,
  ) { }

  ngAfterViewInit(): void {
    this.filterSub = this.loadStat(this.getInitialFiltersForWidget())
      .pipe(takeUntil(this.unSubscribe$))
      .subscribe({
        next: (res: any) => this.updateStat(res),
        error: (err: any) => {
          console.error('Error loading stat:', err);
        },
      });

    this.globalFilterService.filter$
      .pipe(
        takeUntil(this.unSubscribe$),
        switchMap((filters) => {
          if (!this.chartFilterHandler.shouldReload(filters, this.data?.widgetId)) {
            return of(null);
          }

          if (this.data.callApiInDefault === false) {
            let mandatoryFiltersSelected = true;
            let missingMandatoryFilterNames: string[] = [];

            if (this.data.mandatoryFilters && this.data.mandatoryFilters.length > 0) {
              for (const mandatoryFilterKey of this.data.mandatoryFilters) {
                const filterValue = filters.queryParams[mandatoryFilterKey] || filters.requestParams[mandatoryFilterKey];
                if (!filterValue || filterValue.length === 0) {
                  mandatoryFiltersSelected = false;
                  missingMandatoryFilterNames.push(snakeCaseToTitleCase(mandatoryFilterKey));
                }
              }
            }

            if (!mandatoryFiltersSelected) {
              this.hasError = true;
              this.errorMessage = `Please select ${missingMandatoryFilterNames.join(' and ')} to see the data.`;
              return of(null);
            }
          }
          this.hasError = false;
          return this.loadStat(filters);
        })
      )
    .subscribe({
  next: (res: any) => {
    if (res === null) return;

    try {
      this.updateStat(res);

      if (!this.stat) {
        this.hasError = true;
        this.errorMessage = 'No data found for the selected filters.';
      } else {
        this.hasError = false;
      }
    } catch {
      this.hasError = true;
      this.errorMessage = 'Error processing data.';
    }
  },
  error: (err: any) => {
    this.hasError = true;
    this.errorMessage = 'Error loading stat.';
    console.error('Error loading stat:', err);
  },
});
  }
  get formattedTitle(): string {
    return this.data?.title?.replace(/\n/g, '<br>') || '';
  }


  loadStat(filters?: any): any {
    return this.configService.getConfig(this.data?.apiUrl, {
      ...filters,
      useQueryParams: this.data?.useQueryParams || false
    }, this.data?.method || 'post')
  }

  private getInitialFiltersForWidget(): any | undefined {
    const navigationFilters = this.globalFilterService.getNavigationFiltersForWidget(this.data?.widgetId);
    if (navigationFilters) {
      return navigationFilters;
    }

    const filters = this.globalFilterService.getCurrentFilters();
    const hasFilters = !!(
      Object.keys(filters?.queryParams || {}).length ||
      Object.keys(filters?.requestParams || {}).length ||
      filters?.filterListener
    );

    if (!hasFilters) {
      return undefined;
    }

    return this.chartFilterHandler.shouldReload(filters, this.data?.widgetId)
      ? filters
      : undefined;
  }

  private updateStat(res: any): void {
    const normalized =
      res?.responseList?.[0]?.data?.[0] ??
      res?.data?.[0] ??
      res;
    const parent = this.data?.parentKey
      ? normalized?.[this.data?.parentKey] ?? res?.[this.data?.parentKey]
      : normalized;
    const source = parent ?? normalized;
    const rawValue = source?.[this.data.key];

    this.stat = this.data?.useFormatStat
      ? formatStat(
          rawValue,
          this.data?.key || '',
          this.data?.prefix || ''
        )
      : rawValue;
  }

  ngOnDestroy(): void {
    this.filterSub?.unsubscribe();
    this.unSubscribe$.next();
    this.unSubscribe$.complete();
  }
}
